import * as React from "react";
import { cn } from "@/lib/utils";

type Tone = "default" | "success" | "error" | "brand" | "muted" | "white";
type Size = "xl" | "md" | "sm" | "meta";

const toneClass: Record<Tone, string> = {
  default: "text-gray-900",
  success: "text-success-600",
  error: "text-error-600",
  brand: "text-brand-700",
  muted: "text-gray-500",
  white: "text-white",
};

// DM Mono only ships weights 300/400/500, so the guideline "700" caps at 500 (medium).
const sizeClass: Record<Size, string> = {
  xl: "text-[22px] leading-tight font-medium",
  md: "text-base font-medium",
  sm: "text-[13px] font-normal",
  meta: "text-[11px] font-medium",
};

const formatNumber = (n: number, fractionDigits = 0) =>
  new Intl.NumberFormat("tr-TR", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(n);

export interface MonoValueProps extends React.HTMLAttributes<HTMLSpanElement> {
  value: number | string;
  prefix?: string;
  suffix?: string;
  tone?: Tone;
  size?: Size;
  /** Apply tr-TR thousand separators (numbers only). */
  format?: boolean;
  /** Show an explicit +/- sign and auto-color by sign (overrides `tone`). */
  signed?: boolean;
  fractionDigits?: number;
}

/**
 * Renders numeric / financial values in DM Mono per Design System v2026.
 * Use for currency, %, dates, Ada/Parsel, check numbers and entry IDs.
 */
export const MonoValue: React.FC<MonoValueProps> = ({
  value,
  prefix,
  suffix,
  tone = "default",
  size = "md",
  format = false,
  signed = false,
  fractionDigits = 0,
  className,
  ...rest
}) => {
  let display: string;
  let activeTone: Tone = tone;

  if (typeof value === "number") {
    if (signed) activeTone = value > 0 ? "success" : value < 0 ? "error" : tone;
    const sign = signed && value > 0 ? "+" : signed && value < 0 ? "-" : "";
    const abs = signed ? Math.abs(value) : value;
    display = `${sign}${format ? formatNumber(abs, fractionDigits) : String(abs)}`;
  } else {
    display = value;
  }

  return (
    <span
      className={cn("font-mono tabular-nums", sizeClass[size], toneClass[activeTone], className)}
      {...rest}
    >
      {prefix}
      {display}
      {suffix ? <span className="ml-0.5">{suffix}</span> : null}
    </span>
  );
};

export default MonoValue;
